import React, { useState } from 'react';
import { useWindowWidth } from '../hooks/useWindowWidth';
import type { StateNexusData } from '../services/api';
import { US_STATE_PATHS } from './usStatePaths';

const STATE_NAMES: Record<string, string> = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DC: 'D.C.',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
};

type TileKey = 'red' | 'amber' | 'green' | 'grey';

const FILL: Record<TileKey, string> = {
  red: '#E93544',
  amber: '#F78711',
  green: '#1DAF61',
  grey: '#CACFD8',
};
const FILL_SELECTED: Record<TileKey, string> = {
  red: '#C22432',
  amber: '#CE6A05',
  green: '#158A4D',
  grey: '#99A0AE',
};
const LABEL: Record<TileKey, string> = {
  red: '#E93544',
  amber: '#F78711',
  green: '#1DAF61',
  grey: '#99A0AE',
};

const LEGEND: { dot: string; bg: string; text: string; label: string }[] = [
  { dot: '#E93544', bg: '#FFEBEC', text: '#E93544', label: 'Nexus Triggered' },
  { dot: '#E97135', bg: '#FFF1EB', text: '#E97135', label: 'Warning (80%+)' },
  { dot: '#1FC16B', bg: '#E0FAEC', text: '#1FC16B', label: 'Safe' },
  { dot: '#CACFD8', bg: '#F5F7FA', text: '#525866', label: 'No data' },
];

function tileKey(s: StateNexusData | null | undefined): TileKey {
  if (!s) return 'grey';
  if (s.nexusTriggered) return 'red';
  if (s.revenueThresholdPct >= 80) return 'amber';
  return 'green';
}

interface TooltipState {
  code: string;
  s: StateNexusData;
  mx: number;
  my: number;
}

interface USNexusMapProps {
  states: StateNexusData[];
  selectedCode: string | null;
  onStateClick?: (s: StateNexusData) => void;
  loading: boolean;
  error: string | null;
  errorStatus?: number | null;
  onRetry: () => void;
}

export default function USNexusMap({
  states = [],
  selectedCode,
  onStateClick,
  loading,
  error,
  errorStatus,
  onRetry,
}: USNexusMapProps): JSX.Element {
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const mobile = useWindowWidth() < 768;

  const stateMap: Record<string, StateNexusData | undefined> = {};
  for (const s of states) stateMap[s.stateCode] = s;

  if (error) {
    const forbidden = errorStatus === 403;
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 320,
          gap: 12,
          background: forbidden ? '#FFF7ED' : '#FFEBEE',
          border: `1px solid ${forbidden ? '#FED7AA' : '#FFCDD2'}`,
          borderRadius: 12,
          padding: 24,
        }}
      >
        <span style={{ fontSize: 13, color: forbidden ? '#C2410C' : '#E93544', fontWeight: 600 }}>
          {forbidden ? "You don't have permission to view this" : 'Failed to load nexus data'}
        </span>
        <span style={{ fontSize: 12, color: forbidden ? '#C2410C' : '#E93544' }}>
          {forbidden ? 'Ask your organization admin for access.' : error}
        </span>
        {!forbidden && (
          <button
            type="button"
            onClick={onRetry}
            style={{
              padding: '7px 18px',
              background: '#0F172A',
              color: '#fff',
              border: 'none',
              borderRadius: 7,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', width: mobile ? '100%' : 560 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 12,
          gap: 10,
        }}
      >
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#0F172A' }}>
            US nexus exposure map
          </div>
          <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 2 }}>
            Click a state to inspect
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 14,
            alignItems: 'center',
            justifyContent: 'flex-end',
            flexShrink: 0,
          }}
        >
          {LEGEND.map(({ dot, bg, text, label }) => (
            <div
              key={label}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                height: 20,
                padding: '0 10px',
                borderRadius: 10,
                background: bg,
                fontSize: 12,
                fontWeight: 700,
                color: text,
                whiteSpace: 'nowrap',
              }}
            >
              <div
                style={{ width: 6, height: 6, borderRadius: '50%', background: dot, flexShrink: 0 }}
              />
              {label}
            </div>
          ))}
        </div>
      </div>

      <svg
        viewBox="0 0 1000 589"
        role="img"
        aria-label="US nexus exposure map"
        style={{ width: '100%', height: 'auto', display: 'block', overflow: 'visible' }}
      >
        {Object.entries(US_STATE_PATHS).map(([code, d]) => {
          const s = loading ? undefined : stateMap[code];
          const isActive = !loading && !!s;
          const selected = code === selectedCode;
          const hovered = tooltip?.code === code;
          const key = loading ? 'grey' : tileKey(s);
          const fill = loading ? FILL.grey : selected ? FILL_SELECTED[key] : FILL[key];

          return (
            <path
              key={code}
              d={d}
              fill={fill}
              opacity={loading ? 0.6 : 1}
              stroke="#fff"
              strokeWidth={selected ? 2 : hovered ? 1.4 : 0.75}
              style={{
                cursor: isActive ? 'pointer' : 'default',
                transformBox: 'fill-box',
                transformOrigin: 'center',
                transform: selected
                  ? 'scale(1.06)'
                  : hovered && isActive
                    ? 'scale(1.03)'
                    : 'scale(1)',
                transition: 'transform 0.15s ease, fill 0.15s ease',
                filter: selected ? 'drop-shadow(0 0 4px rgba(15,23,42,0.35))' : undefined,
              }}
              role={isActive ? 'button' : undefined}
              tabIndex={isActive ? 0 : undefined}
              aria-label={isActive ? (STATE_NAMES[code] ?? code) : undefined}
              onClick={() => isActive && s && onStateClick && onStateClick(s)}
              onKeyDown={(e) => {
                if (isActive && s && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  onStateClick?.(s);
                }
              }}
              onMouseEnter={(e) => {
                if (isActive && s) setTooltip({ code, s, mx: e.clientX, my: e.clientY });
              }}
              onMouseLeave={() => setTooltip(null)}
            >
              <title>{STATE_NAMES[code] ?? code}</title>
            </path>
          );
        })}
      </svg>

      <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 10, textAlign: 'center' }}>
        {!loading && `${states.length} state${states.length !== 1 ? 's' : ''} tracked`}
      </div>

      {tooltip && (
        <div
          style={{
            position: 'fixed',
            left: tooltip.mx + 14,
            top: tooltip.my - 44,
            background: 'rgba(23,26,36,0.92)',
            borderRadius: 20,
            padding: '8px 14px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
            pointerEvents: 'none',
            zIndex: 9999,
            fontSize: 12,
            minWidth: 150,
          }}
        >
          <div style={{ fontWeight: 700, color: '#fff', marginBottom: 4 }}>
            {STATE_NAMES[tooltip.code] ?? tooltip.code}
          </div>
          <div style={{ color: LABEL[tileKey(tooltip.s)], fontWeight: 600, fontSize: 11 }}>
            {tooltip.s.nexusTriggered
              ? `${(tooltip.s.revenueThresholdPct ?? 0).toFixed(0)}% of threshold reached`
              : (tooltip.s.revenueThresholdPct ?? 0) >= 80
                ? `Warning — ${(tooltip.s.revenueThresholdPct ?? 0).toFixed(0)}%`
                : `Safe — ${(tooltip.s.revenueThresholdPct ?? 0).toFixed(0)}%`}
          </div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 3 }}>
            ${((tooltip.s.revenue ?? 0) / 1000).toFixed(0)}K · $
            {((tooltip.s.revenueThreshold ?? 0) / 1000).toFixed(0)}K threshold
          </div>
        </div>
      )}
    </div>
  );
}
